import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, Ban, Unlock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtTime, fmtDate, money } from "@/lib/api";
import { StatusBadge } from "@/components/Badges";
import DocList from "@/components/DocList";
import PageHeader, { Empty } from "@/components/PageHeader";
import { t } from "@/lib/i18n";
import { ClearTestData } from "@/components/ClearTestData";

const TABS = ["pending", "approved", "declined", "blocked"];

function UserCard({ u, onApprove, onDecline, onBlock }) {
  return (
    <div className="hf-card p-5 hf-rise" data-testid={`admin-user-${u.id}`}>
      <div className="flex flex-col md:flex-row gap-5 justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-900 text-white">{t(u.role)}</span>
            <StatusBadge status={u.status} />
            <span className="font-mono text-xs text-slate-500">{u.public_code} · {fmtTime(u.created_at)}</span>
          </div>
          <p className="font-display font-bold text-xl mt-2 break-words">{u.company_name}</p>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
            <p>{t("Contact:")} <b className="text-slate-900">{u.name}</b></p>
            <p className="break-words">{t("Email:")} <b className="text-slate-900">{u.email}</b></p>
            <p>{t("Phone:")} <b className="text-slate-900">{u.phone}</b></p>
            <p>{t("Reg no:")} <b className="text-slate-900">{u.registration_number || "—"}</b></p>
            <p>{t("VAT:")} <b className="text-slate-900">{u.vat_number || "—"}</b></p>
            <p className="break-words">{t("Address:")} <b className="text-slate-900">{u.address}</b></p>
            <p>{t("Country:")} <b className="text-slate-900">{u.country || "—"}</b> {t("· Currency:")} <b className="text-slate-900">{u.currency || "—"}</b></p>
            <p>{t("Terms accepted:")} <b className="text-slate-900">{u.terms_accepted_at ? `v${u.terms_version} · ${fmtTime(u.terms_accepted_at)}` : "—"}</b></p>
            {u.role === "transporter" && (
              <p className="sm:col-span-2 break-words" data-testid="admin-git-details">{t("GIT:")} <b className="text-slate-900">{u.git_insurance ? `${u.git_insurance.insurer} · Policy ${u.git_insurance.policy_number} · ${money(u.git_insurance.cover_amount, u.git_insurance.currency)} · expires ${fmtDate(u.git_insurance.expiry_date)}` : t("Not provided")}</b></p>
            )}
          </div>
          {u.admin_note && <p className="text-sm text-red-700 mt-2">{t("Note:")} {u.admin_note}</p>}
        </div>
        <div className="md:w-72 shrink-0">
          <p className="hf-label">{t("Documents (")}{u.documents.length})</p>
          <DocList docs={u.documents} testPrefix="admin-doc" />
        </div>
      </div>
      <div className="flex gap-2 mt-5 pt-4 border-t">
        {!["approved", "blocked"].includes(u.status) && <button onClick={() => onApprove(u)} className="hf-btn-primary h-10" data-testid="btn-admin-approve-user"><Check className="w-4 h-4" /> {t("Approve")}</button>}
        {!["declined", "blocked"].includes(u.status) && <button onClick={() => onDecline(u)} className="hf-btn-danger h-10" data-testid="btn-admin-decline-user"><X className="w-4 h-4" /> {t("Decline")}</button>}
        {u.status === "blocked"
          ? <button onClick={() => onBlock(u, false)} className="hf-btn-primary h-10" data-testid="btn-admin-unblock-user"><Unlock className="w-4 h-4" /> {t("Unblock")}</button>
          : <button onClick={() => onBlock(u, true)} className="hf-btn-ghost h-10 border border-red-200 text-red-600 ml-auto" data-testid="btn-admin-block-user"><Ban className="w-4 h-4" /> {t("Block")}</button>}
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState(null);
  const [stats, setStats] = useState({});
  const [declining, setDeclining] = useState(null);
  const [note, setNote] = useState("");
  const [blocking, setBlocking] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/users", { params: { status: tab } }).then((r) => setUsers(r.data));
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const approve = async (u) => {
    try { await api.post(`/admin/users/${u.id}/approve`); toast.success(`${u.company_name} approved`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onBlock = async (u, block) => {
    if (block) return setBlocking(u);
    try { await api.post(`/admin/users/${u.id}/unblock`); toast.success(t("{name} unblocked", { name: u.company_name })); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const block = async () => {
    try { await api.post(`/admin/users/${blocking.id}/block`); toast.success(t("{name} blocked", { name: blocking.company_name })); setBlocking(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const decline = async () => {
    try { await api.post(`/admin/users/${declining.id}/decline`, { note }); toast.success(t("Registration declined")); setDeclining(null); setNote(""); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const STATS = [["Pending", stats.pending], ["Shippers", stats.shippers], ["Transporters", stats.transporters], ["Open loads", stats.open_loads], ["Connections", stats.connections]];

  return (
    <div>
      <PageHeader kicker={t("Governance")} title={t("Registration approvals")} />
      <div className="flex justify-end -mt-4 mb-4"><ClearTestData onCleared={load} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        {STATS.map(([l, v]) => (
          <div key={l} className="hf-card p-4" data-testid={`admin-stat-${l.toLowerCase().replace(" ", "-")}`}>
            <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t(l)}</p>
            <p className="font-display font-black text-3xl mt-1">{v ?? "–"}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1 mb-5">
        {TABS.map((tb) => (
          <button key={tb} onClick={() => setTab(tb)} data-testid={`admin-tab-${tb}`} className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider ${tab === tb ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{t(tb)}</button>
        ))}
      </div>
      <div className="space-y-4">
        {users?.length === 0 && <Empty title={t("No {tab} registrations", { tab: t(tab) })} testId="admin-empty">{t("All caught up.")}</Empty>}
        {users?.map((u) => <UserCard key={u.id} u={u} onApprove={approve} onDecline={setDeclining} onBlock={onBlock} />)}
      </div>
      <Dialog open={!!declining} onOpenChange={(v) => !v && setDeclining(null)}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader><DialogTitle>{t("Decline")} {declining?.company_name}?</DialogTitle></DialogHeader>
          <label className="hf-label">{t("Reason (shared with applicant)")}</label>
          <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="hf-input" placeholder={t("e.g. Insurance certificate expired")} data-testid="decline-note-input" />
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeclining(null)} className="hf-btn-ghost">{t("Cancel")}</button>
            <button onClick={decline} className="hf-btn bg-red-600 text-white hover:bg-red-700" data-testid="btn-confirm-decline">{t("Decline registration")}</button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!blocking} onOpenChange={(v) => !v && setBlocking(null)}>
        <DialogContent data-testid="block-dialog">
          <DialogHeader><DialogTitle>{t("Block")} {blocking?.company_name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("They will be signed out and can't log in, post loads, bid or chat. Their open loads are hidden from the marketplace. Their history stays, and you can unblock them at any time.")}</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setBlocking(null)} className="hf-btn-ghost">{t("Cancel")}</button>
            <button onClick={block} className="hf-btn bg-red-600 text-white hover:bg-red-700" data-testid="btn-confirm-block">{t("Block account")}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
